import React from 'react';
import { useTranslation } from 'react-i18next';

export function TermsAndConditions() {
  const { t } = useTranslation();
  return (
    <div className="auth-terms-content">
      <h2>{t('terms.section1.title', '1. Introduction')}</h2>
      <p>
        {t(
          'terms.section1.p1',
          'These Terms of Service (the "Terms") govern your access to and use of the SendAllFiles service, including our websites, applications, APIs and related services (together, the "Service"). The Service is operated by Qualetech sàrl ("SendAllFiles", "we", "us", "our"), a Swiss limited liability company registered in the Canton of Valais, Switzerland.',
        )}
      </p>
      <p>
        {t(
          'terms.section1.p2',
          'By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Service. If you use the Service on behalf of an organisation, you represent that you are authorised to bind that organisation, and references to "you" include that organisation.',
        )}
      </p>

      <h2>{t('terms.section2.title', '2. Nature of the Service')}</h2>
      <p>
        {t(
          'terms.section2.p1',
          'SendAllFiles is a file transfer and sharing service, not a long‑term storage, archiving or backup service. Files are stored only temporarily in order to enable transmission to recipients and are automatically deleted after a limited retention period or earlier for technical, security, or business reasons.',
        )}
      </p>
      <p>
        {t(
          'terms.section2.p2',
          'We do not accept any obligation or duty to store, preserve, archive, or back up any file, data, or content you upload or transfer via the Service ("Files"). You are solely responsible for maintaining your own backups and records of your Files.',
        )}
      </p>
      <p>
        {t(
          'terms.section2.p3',
          'If you upload Files while using a paid license seat under a Subscription, and that Subscription is later cancelled or expires, or you are unassigned from that license seat, then as of the effective date of such cancellation, expiry or unassignment all Files that are still stored via the Service under that license will be deleted immediately, regardless of any retention period previously selected for those Files in the user interface.',
        )}
      </p>
      <p>
        {t(
          'terms.section2.p4',
          'To the maximum extent permitted by Swiss law, we are not liable for any loss, deletion, corruption, unavailability, or disclosure of Files, including any resulting loss of business, data, revenue, or reputation.',
        )}
      </p>

      <h2>{t('terms.section3.title', '3. Eligibility and Accounts')}</h2>
      <p>
        {t(
          'terms.section3.p1',
          'You must be at least 18 years old (or the age of legal majority in your jurisdiction, if higher) to use the Service. By using the Service, you represent that you meet this requirement.',
        )}
      </p>
      <p>
        {t(
          'terms.section3.p2',
          'Some features of the Service require you to create an account. You must provide accurate and complete information and keep it up to date. You are responsible for safeguarding your login credentials and for all activities that occur under your account.',
        )}
      </p>

      <h2>{t('terms.section4.title', '4. Subscriptions, Plans and Pricing')}</h2>
      <p>
        {t(
          'terms.section4.p1',
          'We may offer different service levels, including free and paid subscription plans (each a "Subscription"). Unless stated otherwise, paid Subscriptions renew automatically at the end of each billing period (monthly or yearly, as applicable) at the then‑current price.',
        )}
      </p>
      <p>
        {t(
          'terms.section4.p2',
          'We may change prices and the features included in each plan at any time at our discretion. For an ongoing paid Subscription, price changes will apply to the next billing period. Where required by law, we will provide prior notice and the opportunity to cancel before the change takes effect.',
        )}
      </p>
      <p>
        {t(
          'terms.section4.p3',
          'Prices may be stated exclusive of VAT or other applicable taxes. You are responsible for any such taxes, which may be charged in addition to the listed prices depending on your billing address and applicable law.',
        )}
      </p>
      <p>
        {t(
          'terms.section4.p4',
          'We reserve the right to convert any feature currently available in a free plan into a paid feature at any time, at our sole discretion. If we do so, we will use reasonable efforts to provide prior notice. Continued use of such features after they become paid features will require an active paid Subscription.',
        )}
      </p>

      <h2>{t('terms.section5.title', '5. Payment and Billing')}</h2>
      <p>
        {t(
          'terms.section5.p1',
          'You authorise us and our payment processors to charge the applicable Subscription fees to your selected payment method on each billing date. You must ensure that your payment information is current and valid. If a payment cannot be processed, we may suspend or cancel your access to paid features until payment is successfully made.',
        )}
      </p>
      <p>
        {t(
          'terms.section5.p2',
          'Except as expressly provided in these Terms or required by mandatory Swiss law, all fees are non‑refundable.',
        )}
      </p>
      <p>
        {t(
          'terms.section5.p3',
          'If you change the number of seats included in a Subscription, you acknowledge that any VAT or similar sales taxes charged on the original invoiced amount will not be refunded and that an adjustment fee equal to 5% of the gross amount of the adjustment, subject to a minimum of USD 0.50, will apply to each such adjustment.',
        )}
      </p>

      <h2>{t('terms.section6.title', '6. Our Right to Suspend or Terminate')}</h2>
      <p>
        {t(
          'terms.section6.p1',
          'We may, at any time and without prior notice, suspend or terminate your access to the Service or your Subscription if you violate these Terms or any applicable law, if you use the Service in a manner we reasonably consider abusive, harmful or risky, or if we are required to do so by law, court order or authority.',
        )}
      </p>
      <p>
        {t(
          'terms.section6.p2',
          'We may also terminate your Subscription or discontinue the Service, or any part of it, at any time for any reason at our discretion. If we terminate a paid Subscription for our convenience (and not due to your breach), we will refund you the prepaid fees corresponding to the unused portion of your current Subscription period. If we terminate or suspend your Subscription due to your breach, we are not obliged to provide any refund.',
        )}
      </p>

      <h2>{t('terms.section7.title', '7. Your Right to Cancel')}</h2>
      <p>
        {t(
          'terms.section7.p1',
          'You may cancel a recurring Subscription at any time via the account or billing settings provided in the Service. Cancellation will take effect at the end of the current billing period. Unless required by mandatory law or expressly stated otherwise by us, we do not provide refunds for partial billing periods when you cancel.',
        )}
      </p>

      <h2>{t('terms.section8.title', '8. Acceptable Use and Illegal Content')}</h2>
      <p>
        {t(
          'terms.section8.p1',
          'You must not use the Service for any unlawful purpose or in violation of any applicable law or regulation. In particular, you must not upload, transmit, share or store Files that are illegal, harmful, defamatory, obscene, hateful, discriminatory, threatening, or otherwise objectionable; that infringe intellectual property or other rights of third parties; or that relate to child sexual abuse, pornography, terrorism, fraud, money laundering or other criminal activity.',
        )}
      </p>
      <p>
        {t(
          'terms.section8.p2',
          'You must not use the Service to harass, threaten, stalk or abuse others; to send spam or malware; to attempt to gain unauthorised access to any systems or data; or to bypass, disable or interfere with any security or technical measures of the Service. We may block, remove or restrict access to Files and report suspected illegal activity or content to the competent authorities at our discretion.',
        )}
      </p>

      <h2>{t('terms.section9.title', '9. User Content and Licences')}</h2>
      <p>
        {t(
          'terms.section9.p1',
          'You retain all rights to the Files you upload or transfer via the Service. By uploading or making Files available, you grant us a non‑exclusive, worldwide, royalty‑free licence to host, store, process, transmit and make the Files available to the recipients you select solely for the purpose of operating and providing the Service.',
        )}
      </p>
      <p>
        {t(
          'terms.section9.p2',
          'You represent and warrant that you have all necessary rights, licences and permissions to upload and share the Files via the Service, and that the Files and your use of them do not infringe any third‑party rights and do not violate any law or these Terms.',
        )}
      </p>

      <h2>{t('terms.section10.title', '10. Intellectual Property of SendAllFiles')}</h2>
      <p>
        {t(
          'terms.section10.p1',
          'The Service, including all software, code, user interfaces, designs, trademarks, logos and other content (excluding your Files), is owned by or licensed to Qualetech sàrl and is protected by applicable intellectual property laws. Subject to your compliance with these Terms and any applicable Subscription restrictions, we grant you a limited, non‑exclusive, non‑transferable, revocable licence to access and use the Service for your internal business or personal purposes.',
        )}
      </p>

      <h2>{t('terms.section11.title', '11. Privacy and Data Protection')}</h2>
      <p>
        {t(
          'terms.section11.p1',
          'You are responsible for complying with any data protection obligations that apply to you as a controller of personal data contained in the Files you upload (for example, when you share personal data of your employees or customers).',
        )}
      </p>

      <h2>{t('terms.section12.title', '12. Service Changes and Availability')}</h2>
      <p>
        {t(
          'terms.section12.p1',
          'We may update, modify, or discontinue the Service (or any part of it) at any time, for any reason, including to improve functionality, fix bugs, address security risks, or comply with law. We do not guarantee uninterrupted or error‑free operation. The Service may be temporarily unavailable due to maintenance, technical issues or events outside our control.',
        )}
      </p>

      <h2>{t('terms.section13.title', '13. Disclaimers')}</h2>
      <p>
        {t(
          'terms.section13.p1',
          'To the maximum extent permitted by Swiss law, the Service is provided on an "as is" and "as available" basis, without warranties of any kind, whether express, implied or statutory, including any implied warranties of merchantability, fitness for a particular purpose, non‑infringement or uninterrupted availability.',
        )}
      </p>

      <h2>{t('terms.section14.title', '14. Limitation of Liability')}</h2>
      <p>
        {t(
          'terms.section14.p1',
          'Nothing in these Terms excludes or limits our liability where such exclusion or limitation is not permitted under mandatory provisions of Swiss law, for example for wilful misconduct or gross negligence, or for injury to life, body or health.',
        )}
      </p>
      <p>
        {t(
          'terms.section14.p2',
          'Subject to the preceding paragraph, we exclude all liability for any indirect, incidental, special, punitive or consequential damages, including loss of profit, revenue, business, contracts, anticipated savings, goodwill, or data, and for any loss, deletion, corruption, unavailability or disclosure of Files, except where directly caused by our wilful misconduct or gross negligence.',
        )}
      </p>
      <p>
        {t(
          'terms.section14.p3',
          'Subject to the above, and regardless of the legal basis of any claim, our aggregate liability to you arising out of or in connection with the Service and these Terms is limited to the total amount of Subscription fees actually paid by you to us for the paid SendAllFiles Subscription in effect at the time of the event giving rise to the claim, for the then‑current Subscription period only. For users of the free Service who have not paid any fees, our aggregate liability is limited to CHF 0.',
        )}
      </p>

      <h2>{t('terms.section15.title', '15. Indemnification')}</h2>
      <p>
        {t(
          'terms.section15.p1',
          'To the extent permitted by applicable law, you agree to indemnify and hold harmless Qualetech sàrl, its affiliates, directors, employees and agents from and against any claims, damages, losses, costs and expenses (including reasonable legal fees) arising out of or in connection with your use of the Service, the Files you upload or transfer, your violation of these Terms, or your infringement of third‑party rights.',
        )}
      </p>

      <h2>{t('terms.section16.title', '16. Governing Law and Jurisdiction')}</h2>
      <p>
        {t(
          'terms.section16.p1',
          'These Terms, and any dispute or claim arising out of or in connection with them or with the Service, are governed exclusively by the substantive laws of Switzerland, excluding its conflict‑of‑laws rules.',
        )}
      </p>
      <p>
        {t(
          'terms.section16.p2',
          'Only Swiss law is applicable. Any reference to foreign laws, consumer rights under non‑Swiss laws, or foreign jurisdictions is excluded to the maximum extent permitted by mandatory Swiss law. The exclusive place of jurisdiction for all disputes arising out of or in connection with these Terms or the Service shall be the competent courts in the Canton of Valais, Switzerland, unless mandatory Swiss law provides for a different place of jurisdiction.',
        )}
      </p>

      <h2>{t('terms.section17.title', '17. Changes to These Terms')}</h2>
      <p>
        {t(
          'terms.section17.p1',
          'We may revise these Terms from time to time. The most current version will always be available on our website and will indicate the date of last update. If we make material changes, we will use reasonable efforts to notify you (for example, by email or an in‑service notice) before the changes take effect. By continuing to use the Service after the new Terms have become effective, you agree to be bound by the updated Terms. If you do not agree, you must stop using the Service and, if applicable, cancel your Subscription.',
        )}
      </p>

      <h2>{t('terms.section18.title', '18. Miscellaneous')}</h2>
      <p>
        {t(
          'terms.section18.p1',
          'These Terms, together with any applicable plan‑specific conditions, constitute the entire agreement between you and us regarding the Service and supersede any prior agreements. If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall remain in full force and effect. Our failure to exercise or enforce any right or provision shall not constitute a waiver. You may not assign or transfer these Terms without our prior written consent. We may assign or transfer these Terms, in whole or in part, to any affiliate or in connection with a merger, acquisition or sale of assets, without your consent.',
        )}
      </p>
    </div>
  );
}

